import './BetbookPosts.less';
import React, { useEffect, useLayoutEffect } from 'react';
import { dispatch, useReframe } from 'nike-re-framejs';

import { navigate } from '@/routing';
import i18n from 'nike-ui/src/common/i18n';
import { useActiveRoute } from 'nike-ui/src/common/routing/NikeRouteContext';
import { COMMUNITY_ANALYSES, BETBOOK_TICKETS, ONB_POST_REGISTRATION } from '@/routing/routes';
import BetbookPostItem from './BetbookPostItem';
import BetbookPostItemLoader from '@/betbook/views/BetbookPostItemLoader';
import Button from '@/common/component/Button';
import EmptyState from 'nike-ui/src/components/empty-state/EmptyState';
import FloatingButton from 'nike-ui/src/components/floating-button/FloatingButton';
import InfiniteScroll from '@/responsive/components/InfiniteScroll/InfiniteScroll';
import LoaderMini from '@/ui/views/LoaderMini';
import MultipleComponentsLoader from '@/common/components/loaders/MultipleComponentsLoader';
import { loadBetbookPosts, loadMoreBetbookPosts, setScroll } from '@/betbook/handlers';
import {
    hasNextPage$,
    initialBetbookPostsLoadingDone$,
    loadingBetbookPostsPending$,
    offset$,
    posts$,
    scrollPosition$
} from '@/betbook/subs';
import { accountVerificationStatus$, loggedIn$ } from '@/account/subs';
import BetbookFilters from '@/betbook/views/BetbookFilters';
import {
    betbookFilterBetCount$,
    betbookFilterExpirationTag$,
    betbookFilterOdds$,
    betbookFilterSportIds$,
    betbookFilterStake$,
    betbookFilterWin$
} from 'nike-ui/src/components/betbook/subs';
import { betbookNewRoute, useBetslipActive } from '@/betbook/utils';
import { BetboookPostsSortTypeOpions, BetbooPostTypeOptions } from 'nike-ui/src/components/betbook/constants';
import { useBetbookLastParam } from 'nike-ui/src/components/betbook/utils';
import clsx from 'clsx';

interface BetbookPostsProps {
    postType: BetbooPostTypeOptions;
    sortType?: BetboookPostsSortTypeOpions;
}
export function BetbookPosts({ postType, sortType }: BetbookPostsProps) {
    const { subscribe } = useReframe();
    const betslipActive = useBetslipActive();
    const offset = subscribe(offset$) || 0;
    const route = useActiveRoute();
    const [lastParam, setLastParam] = useBetbookLastParam();
    const scrollPosition = subscribe(scrollPosition$) || 0;
    const posts = subscribe(posts$);
    const initialLoadingDone = subscribe(initialBetbookPostsLoadingDone$);
    const loadingPending = subscribe(loadingBetbookPostsPending$);
    const hasNextPage = subscribe(hasNextPage$);
    const loggedIn = subscribe(loggedIn$);
    const verificationStatus = subscribe(accountVerificationStatus$);
    const isVerified = verificationStatus === 'VERIFIED';
    const betbookViewPort = document.querySelector('.layout-main-col');
    const floatingBtnDisabled = !loggedIn || (loggedIn && !isVerified);

    const betCount = subscribe(betbookFilterBetCount$);
    const expirationTag = subscribe(betbookFilterExpirationTag$);
    const odds = subscribe(betbookFilterOdds$);
    const sportIds = subscribe(betbookFilterSportIds$);
    const stake = subscribe(betbookFilterStake$);
    const win = subscribe(betbookFilterWin$);

    useLayoutEffect(() => {
        if (betbookViewPort && window.matchMedia('(min-width: 800px)')) {
            betbookViewPort.scrollTo(0, scrollPosition);
        }
    }, [scrollPosition, betbookViewPort]);
    useLayoutEffect(() => {
        if (offset === 0 && (route === COMMUNITY_ANALYSES || route === BETBOOK_TICKETS)) {
            if (window.matchMedia('(max-width: 799px)').matches) {
                window.onbeforeunload = () => {
                    window.scrollTo(0, 0);
                };
                document.body.className = 'betbook-overflow';
            }
        }
    }, [offset, route]);

    useEffect(() => {
        // hacky way to detect other than detail -> list change.
        // for instance: post list -> author's analyses -> detail -> back -> back
        const same = lastParam && lastParam?.sortType === sortType && lastParam?.postType === postType;
        if (!lastParam || !same) {
            if (lastParam?.postType === postType && lastParam?.sortType !== sortType) {
                dispatch(loadBetbookPosts(postType, sortType, sportIds, odds, stake, win, betCount, expirationTag));
            } else {
                dispatch(loadBetbookPosts(postType, sortType));
            }
            setLastParam({ postType, sortType });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastParam, postType, sortType]);

    function handlePositionMemoize() {
        betbookViewPort && dispatch(setScroll(betbookViewPort?.scrollTop));
    }
    function handleNewPost() {
        if (loggedIn) {
            navigate(betbookNewRoute(postType));
        } else {
            dispatch(['login.openScreen']);
        }
    }
    function handleLoadMore() {
        dispatch(loadMoreBetbookPosts(postType));
    }
    return (
        <div onClick={handlePositionMemoize} className={clsx({ 'betslip-visible': betslipActive })}>
            {loggedIn && !isVerified && <NotVerifiedInfo verificationStatus={verificationStatus} />}
            <BetbookFilters postType={postType} />
            {initialLoadingDone ? (
                posts?.length ? (
                    <InfiniteScroll
                        className="betbook-posts"
                        hasMore={hasNextPage && !loadingPending}
                        loadMore={handleLoadMore}
                        loader={<LoaderMini className="infinite-loader betbook-more-posts-loader" key="loader" />}
                        threshold={0}
                        initialLoad={false}
                    >
                        {posts.map(post => (
                            <BetbookPostItem key={post.id} postType={postType} post={post} />
                        ))}
                        <FloatingButton
                            onClick={handleNewPost}
                            dataAtid="betbook-new-post"
                            disabled={floatingBtnDisabled}
                        />
                    </InfiniteScroll>
                ) : (
                    <div className="betbook-posts">
                        <EmptyState text={i18n('betbook.posts.empty')} />
                        <FloatingButton
                            onClick={handleNewPost}
                            dataAtid="betbook-new-post"
                            disabled={floatingBtnDisabled}
                        />
                    </div>
                )
            ) : (
                <div className="betbook-posts">
                    <MultipleComponentsLoader itemsCount={2} componentLoader={BetbookPostItemLoader} />
                </div>
            )}
        </div>
    );
}

export function NotVerifiedInfo({ verificationStatus }) {
    function finishRegistration(e) {
        e.stopPropagation();
        navigate(ONB_POST_REGISTRATION);
    }

    return (
        <div className="betbook-verification">
            {verificationStatus === 'UNVERIFIED' ? i18n('betbook.notVerified.text') : i18n('betbook.verifying.text')}
            {verificationStatus === 'UNVERIFIED' ? (
                <Button className="finish-registration-link" onClick={finishRegistration}>
                    {i18n('registration.leave.action.stay')}
                </Button>
            ) : null}
        </div>
    );
}
